import { getCookie, setCookie, updateCookie, text_truncate, getBrowserType } from '../core/utils.js';

var code = '',
    ccode = '',
    leads_referrer = document.referrer;

/*
  trial-buttons.js

  Ringkasan:
  Script ini meng-intercept (mencegat) klik link Trial ke halaman sign up (my.jurnal.id/.../users/sign_up)
  supaya:
  1) Loader/overlay tampil instan => user tidak klik berkali-kali.
  2) Navigasi lebih stabil: buka di tab baru dengan teknik yang lebih tahan popup-blocker.
  3) Tracking lebih reliabel: simpan ke DB via admin-ajax terlebih dulu, lalu fallback ke Google Sheets
     jika DB gagal (mis. error server / policy / jaringan).

  Kenapa perlu capture phase + dedupe?
  - Di perangkat mobile sering terjadi urutan event: pointerup/touchend lalu click.
    Jika kita tidak dedupe, 1 tap bisa memicu handler 2x.
  - Ada script lain di theme yang mungkin juga mengikat handler klik.
    Dengan capture:true + stopPropagation, kita pastikan alur navigasi tidak "dibajak".

  Catatan penting:
  - Script ini hanya memproses link sign_up tertentu.
  - Untuk elemen lain, script tidak melakukan apa-apa.
*/

export function trialButton() {

  // Prevent double-binding if this function is called multiple times.
  if (window.__jurnalTrialButtonBound) return;
  window.__jurnalTrialButtonBound = true;

  /*
    Konfigurasi endpoint WordPress:
    - Kita ambil base dari <link rel="https://api.w.org/"> jika ada.
    - Lalu turunkan ke URL admin-ajax (wp-admin/admin-ajax.php).
    Alasan: beberapa environment memblokir wp-json (REST) dengan 403, sedangkan admin-ajax biasanya aman.
  */

  const wpApiLink = document.querySelector('link[rel="https://api.w.org/"]');
  const wpJsonBase = (wpApiLink && wpApiLink.href)
    ? wpApiLink.href.replace(/\/?$/, '/')
    : (window.location.origin + '/wp-json/');
  const wpSiteBase = wpJsonBase.replace(/wp-json\/?$/, '');
  const wpAjaxURL = wpSiteBase.replace(/\/?$/, '/') + 'wp-admin/admin-ajax.php';

  const setDisplayImportant = (el, value) => {
    // Paksa display dengan !important supaya overlay benar-benar terlihat meski ada CSS yang override.
    try {
      el.style.setProperty('display', value, 'important');
    } catch (e) {
      el.style.display = value;
    }
  };

  const showLoading = () => {
    // Prioritas 1: gunakan API global jika tersedia (loader-guard.js)
    // supaya semua script punya tampilan loader yang konsisten.
    if (typeof window.__jurnalShowLoading === 'function') {
      window.__jurnalShowLoading();
      return;
    }
    // Fallback: nyalakan semua elemen .has-loading yang ada di halaman.
    document.querySelectorAll('.has-loading').forEach(el => setDisplayImportant(el, 'flex'));
  };

  const hideLoading = () => {
    if (typeof window.__jurnalHideLoading === 'function') {
      window.__jurnalHideLoading();
      return;
    }
    document.querySelectorAll('.has-loading').forEach(el => setDisplayImportant(el, 'none'));
  };

  const scheduleIdle = (fn) => {
    // Menunda pekerjaan non-kritis (tracking / olah cookie) agar tidak mengganggu respons klik.
    // requestIdleCallback bila ada -> lebih ramah performa (INP). fallback setTimeout.
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: 1500 });
    } else {
      setTimeout(fn, 0);
    }
  };

  const browserType = getBrowserType();

  const isMobileUA = (() => {
    // Deteksi sederhana (dan cukup cepat) untuk menentukan device type.
    // Tidak pakai regex super panjang agar tidak berat di path klik.
    const ua = (navigator.userAgent || '').toLowerCase();
    if (ua.includes('ipad')) return true;
    if (ua.includes('iphone')) return true;
    if (ua.includes('ipod')) return true;
    if (ua.includes('android')) return true;
    return ua.includes('mobile');
  })();
  const deviceType = isMobileUA ? 'Mobile' : 'Desktop';

  const makeClickShort = () => {
    // Membuat ID pendek berbasis waktu.
    // Dipakai sebagai "short"/request id (mis. [Req.JDDMMYYhhmmssms]) untuk:
    // - menandai event tracking
    // - memudahkan audit/debug
    const d = new Date();
    const DD = String(d.getDate()).padStart(2, '0');
    const MM = String(d.getMonth() + 1).padStart(2, '0');
    const YY = String(d.getFullYear()).slice(2);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds());
    const ms = String(d.getMilliseconds());
    return DD + MM + YY + hh + mm + ss + ms;
  };

  const postTrialToDbAjax = async (payload) => {
    // DB-first: kirim payload ke endpoint admin-ajax.
    // keepalive:true supaya request tetap dikirim walau tab segera pindah/navigasi.
    const res = await fetch(wpAjaxURL + '?action=jurnal_trial_store', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
      keepalive: true
    });

    if (!res.ok) {
      throw new Error('admin-ajax HTTP ' + res.status);
    }

    const json = await res.json().catch(() => null);
    if (!json || json.success !== true) {
      const msg = json && json.data && json.data.message ? json.data.message : 'admin-ajax invalid response';
      throw new Error(msg);
    }

    return json;
  };

  const postTrialToGSheets = (payload, gsheetsURL) => {
    // Fallback: POST FormData ke Google Apps Script.
    // FormData dipilih karena kompatibel luas dan mudah untuk endpoint GSheets.
    const fd = new FormData();
    fd.append('Short', payload.short || '');
    fd.append('GCLID', payload.gclid || '');
    fd.append('Journey', payload.journey || '');
    fd.append('UTM Term', payload.utm_term || '');
    fd.append('Matchtype', payload.matchtype || '');
    fd.append('Device', payload.device || '');
    fd.append('Button Label & Button ID', payload.button_label_id || '');
    fd.append('Button Journey', payload.button_label_id || '');
    fd.append('GAID', payload.gaid || '');
    fd.append('utm_source', payload.utm_source || '');
    fd.append('utm_medium', payload.utm_medium || '');
    fd.append('utm_campaign', payload.utm_campaign || '');
    fd.append('Banner ID', payload.banner_id || '');
    fd.append('Waba Medium', payload.waba_medium || '');
    fd.append('Browser', payload.browser || '');
    fd.append('Domain', window.location.host || '');
    fd.append('GCLID Journey', payload.gclid_journey || '');
    fd.append('Referrer', payload.referrer || '');
    fd.append('Query Search Term', payload.query_search_term || '');
    fd.append('Visitor ID', payload.visitor_id || '');

    return fetch(gsheetsURL, { method: 'POST', body: fd, keepalive: true }).catch(() => {});
  };

  const scheduleTrialSendToDbThenFallback = (payload, gsheetsURL) => {
    // Jalankan tracking di waktu idle agar klik tetap responsif.
    // Urutan: DB -> (kalau gagal) GSheets.
    scheduleIdle(() => {
      postTrialToDbAjax(payload)
        .catch((errAjax) => {
          console.error('Trial DB post failed, fallback to GSheets:', errAjax && errAjax.message ? errAjax.message : errAjax);
          postTrialToGSheets(payload, gsheetsURL);
        });
    });
  };

  const getLinkFromEvent = (e) => {
    // Ambil elemen <a> terdekat dari target event.
    // target event bisa Text node => dinormalisasi.
    const rawTarget = e && e.target;
    const target = (rawTarget && rawTarget.nodeType === 3) ? rawTarget.parentElement : rawTarget;
    if (!target || typeof target.closest !== 'function') return null;
    return target.closest('a');
  };

  let _lastHandledTs = 0;
  let _lastHandledEl = null;

  const clickHandler = (e) => {
    // Handler utama: intercept klik Trial.
    // - Hanya memproses jika link mengarah ke sign_up.
    // - Dedupe untuk menghindari 1 tap menghasilkan 2-3 event.
    if (!e) return;
    if (e.__jurnalTrialHandled) return;

    const link = getLinkFromEvent(e);
    if (!link) return;

    const href = link.href || '';
    if (!href.includes('my.jurnal.id/id/users/sign_up') && !href.includes('my.jurnal.id/en/users/sign_up')) return;

    // Blok event sintetis/programmatic (mis. `el.click()` / `dispatchEvent`).
    try {
      if (typeof e.isTrusted === 'boolean' && e.isTrusted === false) {
        try { document.documentElement.dataset.jurnalTrialBlockedSynthetic = String(Date.now()); } catch (err) {}
        try { e.__jurnalTrialHandled = true; } catch (err) {}
        try { e.preventDefault(); } catch (err) {}
        try { if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation(); } catch (err) {}
        try { if (typeof e.stopPropagation === 'function') e.stopPropagation(); } catch (err) {}
        try { showLoading(); } catch (err) {}
        return;
      }
    } catch (err) {}

    const now = (window.performance && typeof window.performance.now === 'function')
      ? window.performance.now()
      : Date.now();

    if (_lastHandledEl === link && (now - _lastHandledTs) < 900) {
      try { e.__jurnalTrialHandled = true; } catch (err) {}
      try { e.preventDefault(); } catch (err) {}
      try { if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation(); } catch (err) {}
      try { if (typeof e.stopPropagation === 'function') e.stopPropagation(); } catch (err) {}
      try { showLoading(); } catch (err) {}
      return;
    }
    _lastHandledEl = link;
    _lastHandledTs = now;

    e.__jurnalTrialHandled = true;
    try { e.preventDefault(); } catch (err) {}
    try { if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation(); } catch (err) {}
    try { if (typeof e.stopPropagation === 'function') e.stopPropagation(); } catch (err) {}

    // 1. Show loader as soon as possible (sync)
    showLoading();

    // 2. Open new tab synchronously within user gesture (required to avoid popup blockers).
    // Pola sama dengan waba-trigger.js:
    // - window.open('about:blank') dipanggil SYNCHRONOUS di dalam handler user gesture.
    // - Popup blocker mengizinkan ini karena masih dalam gesture context.
    // - Jangan gunakan synthetic <a>.click() karena itu isTrusted=false dan akan
    //   diblokir oleh handler kita sendiri.
    const newTab = window.open('about:blank', '_blank');
    const $url = href;
    if (newTab) {
      try { newTab.location.href = $url; } catch (e1) {}
      if (browserType === 'Apple Safari (iOS)') {
        setTimeout(() => {
          try { newTab.location.href = $url; } catch (e2) {}
        }, 200);
      }
      try { newTab.focus(); } catch (e3) {}
    } else {
      window.location.href = $url;
    }

    // 3. Defer all heavy work (cookie, tracking, network) to microtask/idle
    scheduleIdle(() => {
      try {
        const url = new URL(window.location.href);
        const sp = url.searchParams;
        const cookieCache = new Map();
        const gc = (key) => {
          if (cookieCache.has(key)) return cookieCache.get(key);
          const val = getCookie(key) || '';
          cookieCache.set(key, val);
          return val;
        };
        const click_sh = makeClickShort();
        const gclid = sp.get('gclid') || gc('gclid');
        const msclkid = sp.get('msclkid') || gc('msclkid');
        const umedium = sp.get('utm_medium') || gc('utm_medium');
        const ucampaign = sp.get('utm_campaign') || gc('utm_campaign');
        const usource = sp.get('utm_source') || gc('utm_source');
        const utm_term = sp.get('utm_term') || gc('utm_term');
        const matchtype = sp.get('matchtype') || gc('matchtype');
        const banner_journey = gc('banner_journey');
        const cta_journey = gc('cta_journey');
        const cta_last_click = gc('cta_last_click');
        const medium_code = gc('waba_code');
        const query_keyword = sp.get('query') || gc('query_keyword') || 'null';
        let label = '';
        if (link.classList.contains('ic-wa')) {
          label = 'ID: ' + (link.id || '') + ', Label: Sticky WABA';
        } else if (link.classList.contains('top-banner-sticky') || link.classList.contains('top-banner-bot')) {
          label = 'ID: ' + (link.id || '') + ', Label: Sticky Banner';
        } else if (link.id) {
          label = 'ID: ' + link.id + ', Label: ' + (link.innerText || '');
        } else {
          label = 'ID: , Label: ' + (link.innerText || '');
        }
        let cta_journey_temp = '';
        if (cta_journey) {
          if (cta_last_click) {
            cta_journey_temp = (cta_last_click !== (link.id || '')) ? (cta_journey + '|' + label) : cta_journey;
          } else {
            cta_journey_temp = label;
          }
        } else {
          cta_journey_temp = label;
        }
        if (cta_journey) {
          updateCookie('cta_journey', cta_journey_temp);
        } else {
          setCookie('cta_journey', cta_journey_temp, 31);
        }
        if (cta_last_click) {
          updateCookie('cta_last_click', link.id || '');
        } else {
          setCookie('cta_last_click', link.id || '', 31);
        }
        const modifiedCampaign = '[Req.J' + click_sh + ']';
        const campaignInfo = {
          gclid: gclid || '',
          utm_source: usource || '',
          utm_medium: umedium || '',
          utm_campaign: modifiedCampaign
        };
        const cookiesCampaignInfo = encodeURIComponent(JSON.stringify(campaignInfo));
        if (gc('created_at')) {
          updateCookie('campaign_info', cookiesCampaignInfo);
          updateCookie('j_campaign_info', cookiesCampaignInfo);
        } else {
          setCookie('campaign_info', cookiesCampaignInfo, 31);
          setCookie('j_campaign_info', cookiesCampaignInfo, 31);
        }
        const payload = {
          short: click_sh,
          gclid: text_truncate(`${gclid}|${msclkid}`, 250, ''),
          journey: (gc('user_journey') ? text_truncate(gc('user_journey'), 250, '') : ''),
          utm_term: utm_term,
          matchtype: matchtype,
          device: deviceType,
          button_label_id: cta_journey_temp,
          gaid: (gc('gaid') ? gc('gaid') : (gc('_ga') ? gc('_ga').slice(6) : '')),
          utm_source: usource,
          utm_medium: umedium,
          utm_campaign: ucampaign,
          utm_content: (sp.get('utm_content') || gc('utm_content') || ''),
          banner_id: banner_journey || '',
          waba_medium: medium_code || '',
          browser: browserType,
          gclid_journey: (gc('gclid_journey') || ''),
          referrer: (gc('lead_referrer') ? gc('lead_referrer') : (leads_referrer || '')),
          query_search_term: query_keyword,
          visitor_id: (gc('visitor_id') || '')
        };
        const scriptURL = 'https://script.google.com/macros/s/AKfycbxEjYVMgbE9GCMJnnpLgwgkcph8xbe1d2a4CQpz2UzF9ijordtPNLpLdOEzHZ3Ur5E/exec';
        postTrialToDbAjax(payload)
          .catch((errAjax) => {
            console.error('Trial DB post failed, fallback to GSheets:', errAjax && errAjax.message ? errAjax.message : errAjax);
            postTrialToGSheets(payload, scriptURL);
          });
      } catch (err) {
        // no-op
      }
    });

    setTimeout(hideLoading, 1500);
    return;
  };

  const bind = (target, type) => {
    try {
      // passive:false karena kita memanggil preventDefault.
      // capture:true agar handler ini berjalan lebih dulu.
      target.addEventListener(type, clickHandler, { passive: false, capture: true });
    } catch (e) {}
  };

  // Bind early in the event path to avoid other handlers stopping propagation.
  // Kita bind pada window dan document untuk meningkatkan peluang "kena" event
  // meski ada elemen lain yang stopPropagation.
  ['click', 'pointerup', 'touchend'].forEach((type) => {
    bind(window, type);
    bind(document, type);
  });
}